import React, { useState, useEffect, useCallback } from 'react';
import UsersIcon from './icons/UsersIcon';
import BriefcaseIcon from './icons/BriefcaseIcon';
import FireIcon from './icons/FireIcon';
import SparklesIcon from './icons/SparklesIcon';
import CloseIcon from './icons/CloseIcon';
import * as api from '../services/apiService';
import { formatTimestamp } from '../utils/formatTimestamp';

const AdminDashboard: React.FC = () => {
    const [stats, setStats] = useState<any>(null);
    const [loading, setLoading] = useState(true);
    const [drillFilter, setDrillFilter] = useState<{ filter: string; label: string } | null>(null);
    const [drillUsers, setDrillUsers] = useState<any[]>([]);
    const [drillLoading, setDrillLoading] = useState(false);

    useEffect(() => {
        api.getAdminDashboardStats()
            .then(setStats)
            .finally(() => setLoading(false));
    }, []);

    const openDrill = useCallback((filter: string, label: string) => {
        setDrillFilter({ filter, label });
        setDrillLoading(true);
        setDrillUsers([]);
        api.getAdminUsersByFilter(filter)
            .then(data => setDrillUsers(data.users || []))
            .finally(() => setDrillLoading(false));
    }, []);

    const closeDrill = useCallback(() => {
        setDrillFilter(null);
        setDrillUsers([]);
    }, []);

    if (loading) return <div className="p-8 text-center text-brand-muted animate-pulse font-urbanist">Loading system analytics...</div>;
    if (!stats) return <div className="p-8 text-center text-red-400 font-urbanist">Failed to load system data.</div>;

    const totalUsers = stats.totalUsers || 0;
    const cluster = stats.clusterStats || {};

    return (
        <div className="space-y-6 font-urbanist">
            <div className="bg-brand-surface rounded-lg p-6 border border-gray-800">
                <h2 className="text-3xl font-extrabold text-white mb-2 tracking-tight">System Command</h2>
                <p className="text-brand-muted">Platform health and active user distribution.</p>
            </div>

            {/* KPI Cards */}
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
                <KpiCard label="Total Members" value={totalUsers} icon={<UsersIcon className="h-6 w-6 text-brand-orange" />} onClick={() => openDrill('all', 'All Members')} />
                <KpiCard label="Talent Hub" value={cluster.talent || 0} icon={<UsersIcon className="h-6 w-6 text-blue-400" />} onClick={() => openDrill('talent', 'Talent Hub')} />
                <KpiCard label="Businesses" value={cluster.business || 0} icon={<BriefcaseIcon className="h-6 w-6 text-green-400" />} onClick={() => openDrill('business', 'Businesses')} />
                <KpiCard label="Churn Signals" value={stats.churnCount || 0} icon={<FireIcon className="h-6 w-6 text-yellow-400" />} onClick={() => openDrill('churn', 'Churn Signals')} />
                <KpiCard label="Frozen/Inactive" value={stats.frozenCount || 0} icon={<FireIcon className="h-6 w-6 text-red-500" />} onClick={() => openDrill('frozen', 'Frozen / Inactive')} />
            </div>

            {/* Cluster Breakdown */}
            <div className="bg-brand-surface rounded-lg p-6 border border-gray-800">
                <h3 className="text-xl font-bold text-white mb-6 tracking-tight">Cluster Distribution</h3>
                <div className="space-y-5">
                    <Bar label="Talent & Performers"    value={cluster.talent   || 0} total={totalUsers} color="bg-blue-500"   onClick={() => openDrill('talent',   'Talent & Performers')} />
                    <Bar label="Businesses & Labels"    value={cluster.business || 0} total={totalUsers} color="bg-green-500"  onClick={() => openDrill('business', 'Businesses & Labels')} />
                    <Bar label="Production & Services"  value={cluster.services || 0} total={totalUsers} color="bg-purple-500" onClick={() => openDrill('service',  'Production & Services')} />
                    <Bar label="Education & Schools"    value={cluster.edu      || 0} total={totalUsers} color="bg-yellow-500" onClick={() => openDrill('edu',      'Education & Schools')} />
                    <Bar label="Fan Community"          value={cluster.fan      || 0} total={totalUsers} color="bg-pink-500"   onClick={() => openDrill('fan',      'Fan Community')} />
                    <Bar label="Multi-cluster"          value={cluster.multi    || 0} total={totalUsers} color="bg-orange-500" onClick={() => openDrill('multi',    'Multi-cluster')} />
                </div>
            </div>

            {/* Voice of the Artist — full width */}
            <div className="bg-brand-surface rounded-lg p-6 border border-gray-800">
                <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                        <SparklesIcon className="h-6 w-6 text-brand-orange" />
                        <h3 className="text-xl font-bold text-white tracking-tight">Voice of the Artist</h3>
                    </div>
                    {stats.feedback?.length > 0 && (
                        <span className="text-xs font-mono bg-brand-orange/10 text-brand-orange px-3 py-1 rounded-full border border-brand-orange/20">
                            {stats.feedback.length} messages
                        </span>
                    )}
                </div>
                <p className="text-sm text-brand-muted mb-6">Suggestions and feedback from your community.</p>

                {stats.feedback?.length > 0 ? (
                    <div className="space-y-4">
                        {stats.feedback.map((f: any) => (
                            <FeedbackCard key={f.id} f={f} />
                        ))}
                    </div>
                ) : (
                    <div className="text-center py-16 border-2 border-dashed border-gray-800 rounded-xl">
                        <p className="text-brand-muted italic">No feedback received yet.</p>
                    </div>
                )}
            </div>

            {/* Drill-down Drawer */}
            <DrillDrawer
                open={!!drillFilter}
                label={drillFilter?.label ?? ''}
                users={drillUsers}
                loading={drillLoading}
                onClose={closeDrill}
            />
        </div>
    );
};

/* ─── Drill-down Drawer ─────────────────────────────────────────────────── */

const DrillDrawer: React.FC<{
    open: boolean;
    label: string;
    users: any[];
    loading: boolean;
    onClose: () => void;
}> = ({ open, label, users, loading, onClose }) => {
    if (!open) return null;
    return (
        <>
            <div className="fixed inset-0 bg-black/60 z-40 backdrop-blur-sm" onClick={onClose} />
            <div className="fixed right-0 top-0 h-full w-full max-w-md bg-brand-surface border-l border-gray-800 shadow-2xl z-50 flex flex-col">
                <div className="flex items-center justify-between p-6 border-b border-gray-800 flex-shrink-0">
                    <div>
                        <h3 className="text-xl font-bold text-white font-urbanist">{label}</h3>
                        <p className="text-sm text-brand-muted font-urbanist">
                            {loading ? 'Loading...' : `${users.length} user${users.length !== 1 ? 's' : ''}`}
                        </p>
                    </div>
                    <button onClick={onClose} className="p-2 rounded-lg hover:bg-gray-800 transition-colors text-brand-muted hover:text-white">
                        <CloseIcon className="h-5 w-5" />
                    </button>
                </div>
                <div className="flex-1 overflow-y-auto p-6">
                    {loading ? (
                        <div className="text-center py-12 text-brand-muted animate-pulse font-urbanist">Loading users...</div>
                    ) : users.length === 0 ? (
                        <div className="text-center py-12 text-brand-muted italic font-urbanist">No users in this group.</div>
                    ) : (
                        <ul className="space-y-3">
                            {users.map((u: any) => (
                                <li key={u.handle} className="flex items-center gap-3 p-3 bg-gray-800/40 rounded-xl border border-gray-800">
                                    {u.avatarUrl ? (
                                        <img src={u.avatarUrl} className="h-10 w-10 rounded-full border border-gray-700 flex-shrink-0" alt="" />
                                    ) : (
                                        <div className="h-10 w-10 rounded-full bg-gray-700 border border-gray-600 flex-shrink-0 flex items-center justify-center">
                                            <span className="text-xs font-bold text-gray-400">{(u.name || u.handle || '?')[0].toUpperCase()}</span>
                                        </div>
                                    )}
                                    <div className="flex-1 min-w-0">
                                        <p className="font-bold text-white text-sm truncate font-urbanist">{u.name}</p>
                                        <p className="text-[10px] text-brand-muted font-mono">@{u.handle}</p>
                                    </div>
                                    {u.subscriptionTier && TIER_LABEL[u.subscriptionTier] && (
                                        <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full border flex-shrink-0 ${TIER_LABEL[u.subscriptionTier].color}`}>
                                            {TIER_LABEL[u.subscriptionTier].label}
                                        </span>
                                    )}
                                </li>
                            ))}
                        </ul>
                    )}
                </div>
            </div>
        </>
    );
};

/* ─── Shared constants ──────────────────────────────────────────────────── */

const PRO      = 'bg-blue-500/10 text-blue-400 border-blue-500/20';
const PRO_PLUS = 'bg-green-500/10 text-green-400 border-green-500/20';

const TIER_LABEL: Record<string, { label: string; color: string }> = {
    talent_pro:          { label: 'Talent Pro',   color: PRO },
    talent_pro_plus:     { label: 'Talent Pro+',  color: PRO_PLUS },
    business_pro:        { label: 'Biz Pro',      color: PRO },
    business_pro_plus:   { label: 'Biz Pro+',     color: PRO_PLUS },
    service_pro:         { label: 'Svc Pro',      color: PRO },
    service_pro_plus:    { label: 'Svc Pro+',     color: PRO_PLUS },
    edu_pro:             { label: 'Edu Pro',       color: PRO },
    edu_pro_plus:        { label: 'Edu Pro+',      color: PRO_PLUS },
    multi_pro:           { label: 'Multi Pro',    color: PRO },
    multi_pro_plus:      { label: 'Multi Pro+',   color: PRO_PLUS },
    fan_basic:           { label: 'Fan',          color: 'bg-pink-500/10 text-pink-400 border-pink-500/20' },
};

/* ─── Sub-components ────────────────────────────────────────────────────── */

const FeedbackCard: React.FC<{ f: any }> = ({ f }) => {
    const tier = f.subscriptionTier ? TIER_LABEL[f.subscriptionTier] : null;
    return (
        <div className="bg-gray-800/40 p-4 rounded-xl border border-gray-800 flex gap-4">
            {f.avatarUrl ? (
                <img src={f.avatarUrl} className="h-10 w-10 rounded-full border border-gray-700 flex-shrink-0 mt-0.5" alt="" />
            ) : (
                <div className="h-10 w-10 rounded-full bg-gray-700 border border-gray-600 flex-shrink-0 mt-0.5 flex items-center justify-center">
                    <span className="text-xs font-bold text-gray-400">{(f.userName || f.userId || '?')[0].toUpperCase()}</span>
                </div>
            )}
            <div className="flex-1 min-w-0">
                <div className="flex flex-wrap items-center gap-2 mb-1">
                    <span className="font-bold text-white text-sm">{f.userName || f.userId}</span>
                    <span className="text-[10px] text-brand-muted font-mono">@{f.userId}</span>
                    {tier && (
                        <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full border ${tier.color}`}>{tier.label}</span>
                    )}
                    <span className="text-[10px] text-brand-muted font-mono ml-auto">{f.timestamp ? formatTimestamp(f.timestamp) : '—'}</span>
                </div>
                <p className="text-gray-300 text-sm leading-relaxed">{f.text}</p>
            </div>
        </div>
    );
};

const KpiCard: React.FC<{ label: string; value: string | number; icon: React.ReactNode; onClick?: () => void }> = ({ label, value, icon, onClick }) => (
    <div
        onClick={onClick}
        className={`bg-brand-surface rounded-xl p-5 flex items-center space-x-4 border border-gray-800 shadow-sm transition-colors ${onClick ? 'cursor-pointer hover:border-brand-orange' : ''}`}
    >
        <div className="bg-gray-800 p-3 rounded-xl border border-gray-700">{icon}</div>
        <div>
            <p className="text-xs font-bold text-brand-muted tracking-widest">{label}</p>
            <p className="text-2xl font-bold text-white">{value}</p>
        </div>
    </div>
);

const Bar: React.FC<{ label: string; value: number; total: number; color: string; onClick?: () => void }> = ({ label, value, total, color, onClick }) => {
    const percent = total > 0 ? Math.round((value / total) * 100) : 0;
    return (
        <div className={onClick ? 'cursor-pointer group' : ''} onClick={onClick}>
            <div className="flex justify-between text-xs font-bold mb-2 tracking-widest">
                <span className={`text-white ${onClick ? 'group-hover:text-brand-orange transition-colors' : ''}`}>{label}</span>
                <span className="text-brand-muted">{value} ({percent}%)</span>
            </div>
            <div className="w-full bg-gray-800 rounded-full h-2 border border-gray-700">
                <div className={`h-2 rounded-full ${color} transition-all duration-1000 shadow-sm`} style={{ width: `${percent}%` }}></div>
            </div>
        </div>
    );
};

export default AdminDashboard;
